import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, AlertCircle } from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';

/**
 * Stripe Connect Success Page
 *
 * This page is displayed after a restaurant owner completes Stripe Express onboarding.
 * It checks the account status and guides them back to the setup wizard.
 */
export default function StripeConnectSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const restAccountId = searchParams.get('rest_account_id');

  const [status, setStatus] = useState<'checking' | 'success' | 'pending' | 'error'>('checking');
  const [accountStatus, setAccountStatus] = useState<any>(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    checkStripeStatus();
  }, []);

  useEffect(() => {
    // Auto-redirect countdown
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      handleContinue();
    }
  }, [countdown, status]);

  const checkStripeStatus = async () => {
    try {
      const response = await apiClient.get('/stripe-connect/account-status');
      const data = response.data;

      setAccountStatus(data);

      // Determine status based on Stripe account state
      if (data.charges_enabled && data.payouts_enabled) {
        setStatus('success');
      } else if (data.details_submitted) {
        setStatus('pending');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      setStatus('error');
    }
  };

  const handleContinue = () => {
    // Redirect to dashboard - DashboardRouter will show AI Host Control Center
    navigate('/restaurant-dashboard');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Stripe Connect Setup
          </h1>
          <p className="text-gray-600">
            We're checking your account status...
          </p>
        </div>

        {/* Status Display */}
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          {status === 'checking' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-lg text-gray-700 font-semibold">
                Verifying your Stripe account...
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This will only take a moment
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Setup Complete! 🎉
              </h2>
              <p className="text-gray-600 mb-6">
                Your Stripe Express account has been successfully verified.
                You can now accept AI Host orders and receive payments directly.
              </p>

              {/* Account Details */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-green-900 mb-2">Account Status</h3>
                <ul className="space-y-1 text-sm text-green-800">
                  <li>✅ Card payments enabled</li>
                  <li>✅ Payouts enabled</li>
                  <li>✅ Account fully verified</li>
                </ul>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Redirecting to AI Host Control Center in {countdown} seconds...
              </p>

              <button
                onClick={handleContinue}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
              >
                Go to Control Center
              </button>
            </div>
          )}

          {status === 'pending' && (
            <div className="text-center">
              <AlertCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verification Pending
              </h2>
              <p className="text-gray-600 mb-6">
                Your Stripe account is being reviewed. This typically takes 1-2 business days.
              </p>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-yellow-900 mb-2">Current Status</h3>
                <ul className="space-y-1 text-sm text-yellow-800">
                  <li>{accountStatus?.charges_enabled ? '✅' : '⏳'} Card payments {accountStatus?.charges_enabled ? 'enabled' : 'pending'}</li>
                  <li>{accountStatus?.payouts_enabled ? '✅' : '⏳'} Payouts {accountStatus?.payouts_enabled ? 'enabled' : 'pending'}</li>
                  <li>{accountStatus?.details_submitted ? '✅' : '⏳'} Information {accountStatus?.details_submitted ? 'submitted' : 'required'}</li>
                </ul>
              </div>

              <p className="text-sm text-gray-500 mb-4">
                You can continue and start configuring AI Host. We'll notify you when verification is complete.
              </p>

              <button
                onClick={handleContinue}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
              >
                Go to Control Center
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Setup Incomplete
              </h2>
              <p className="text-gray-600 mb-6">
                We couldn't verify your Stripe account. You may need to complete additional steps.
              </p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-red-900 mb-2">Next Steps</h3>
                <ul className="space-y-1 text-sm text-red-800">
                  <li>• Return to the setup wizard</li>
                  <li>• Try connecting your Stripe account again</li>
                  <li>• Make sure to complete all required fields</li>
                </ul>
              </div>

              <button
                onClick={handleContinue}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact support at{' '}
            <a href="mailto:support@dineai.ai" className="text-purple-600 hover:underline">
              support@dineai.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
